import {incrementIssuesTabCounter} from '@github-ui/issues-tab-counter'
import {commitMutation, graphql} from 'react-relay'
import type {Environment, RecordSourceProxy, RecordSourceSelectorProxy} from 'relay-runtime'

import {TEMP_SUB_ISSUE_ID_PREFIX} from '../constants/values'
import type {CreatedIssue, SubIssueSummaryData} from '../utils/model'
import type {
  CreateIssueInput,
  createIssueMutation,
  createIssueMutation$data,
} from './__generated__/createIssueMutation.graphql'

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

type IssueMutationProps = {
  environment: Environment
  input: CreateIssueInput
  onError?: (error: Error) => void
  onCompleted?: (response: createIssueMutation$data) => void
}

type IssueMutationWithPromiseProps = {
  environment: Environment
  issueMetadata: IssueMetadata
  onError?: (error: Error) => void
  onCompleted?: (response: createIssueMutation$data) => void
}

export type IssueMetadata = {
  assigneeIds?: string[] | null | undefined
  body?: string | null | undefined
  issueTemplate?: string | null | undefined
  issueTypeId?: string | null | undefined
  labelIds?: string[] | null | undefined
  milestoneId?: string | null | undefined
  parentIssueId?: string | null | undefined
  projectIds?: string[] | null | undefined
  repositoryId: string
  title: string
}

export type IssueMutationResponse = {
  issue: CreatedIssue
  errors?: Array<{
    message: string
  }>
}

function extractMutationResponse(response: createIssueMutation$data): IssueMutationResponse {
  const {createIssue} = response
  if (!createIssue) {
    return {
      issue: {} as CreatedIssue,
      errors: [
        {
          message: 'An unknown error occurred while creating the issue.',
        },
      ],
    }
  }

  const {issue} = createIssue
  if (!issue) {
    return {
      issue: {} as CreatedIssue,
      errors: [
        {
          message: 'An unknown error occurred while creating the issue.',
        },
      ],
    }
  }

  return {
    issue,
    errors: createIssue.errors?.map(error => ({message: error.message})),
  }
}

/**
 * Gets or creates the subIssues connection on a parent issue record.
 */
function getOrCreateSubIssuesConnection(store: RecordSourceProxy, parentRecord: ReturnType<RecordSourceProxy['get']>) {
  if (!parentRecord) return null

  const subIssuesConnection = parentRecord.getLinkedRecord('subIssues', {first: 100})
  if (!subIssuesConnection) {
    return null
  }
  return subIssuesConnection
}

/**
 * Creates an optimistic assignees connection for a sub-issue.
 */
function createOptimisticAssigneesConnection(
  store: RecordSourceProxy,
  optimisticId: string,
  assigneeIds: readonly string[] | null | undefined,
) {
  const assigneesId = `${optimisticId}-assignees`
  const assigneesConnection = store.create(assigneesId, 'UserConnection')

  const assigneeEdges: Array<ReturnType<typeof store.create>> = []
  if (assigneeIds && assigneeIds.length > 0) {
    for (const assigneeId of assigneeIds) {
      const userRecord = store.get(assigneeId)
      if (userRecord) {
        const edgeId = `${optimisticId}-assignee-edge-${assigneeId}`
        const edge = store.create(edgeId, 'UserEdge')
        edge.setLinkedRecord(userRecord, 'node')
        assigneeEdges.push(edge)
      }
    }
  }

  assigneesConnection.setValue(assigneeEdges.length, 'totalCount')
  assigneesConnection.setLinkedRecords(assigneeEdges, 'edges')
  return assigneesConnection
}

/**
 * Creates an optimistic sub-issue record with all required fields for SubIssuesListItem fragment.
 */
function createOptimisticSubIssue(
  store: RecordSourceProxy,
  inputHash: CreateIssueInput,
  parentRecord: NonNullable<ReturnType<RecordSourceProxy['get']>>,
) {
  const optimisticId = `${TEMP_SUB_ISSUE_ID_PREFIX}${Date.now()}`
  const newSubIssue = store.create(optimisticId, 'Issue')
  newSubIssue.setValue(optimisticId, 'id')
  newSubIssue.setValue(inputHash.title, 'title')
  newSubIssue.setValue(escapeHtml(inputHash.title), 'titleHTML')
  newSubIssue.setValue('OPEN', 'state')
  newSubIssue.setValue(null, 'stateReason')

  // Create and attach assignees connection
  const assigneesConnection = createOptimisticAssigneesConnection(store, optimisticId, inputHash.assigneeIds)
  newSubIssue.setLinkedRecord(assigneesConnection, 'assignees', {first: 10})

  // Set repository (use input's repository or fallback to parent's)
  const repoRecord = store.get(inputHash.repositoryId)
  if (repoRecord) {
    newSubIssue.setLinkedRecord(repoRecord, 'repository')
  } else {
    const parentRepo = parentRecord.getLinkedRecord('repository')
    if (parentRepo) {
      newSubIssue.setLinkedRecord(parentRepo, 'repository')
    }
  }

  // Set URL (empty for optimistic)
  newSubIssue.setValue('', 'url')

  // Create issueDependenciesSummary (required by SubIssueStateIcon)
  const depsId = `${optimisticId}-deps`
  const depsSummary = store.create(depsId, 'IssueDependenciesSummary')
  depsSummary.setValue(0, 'blockedBy')
  newSubIssue.setLinkedRecord(depsSummary, 'issueDependenciesSummary')

  // Create subIssuesSummary (required by SubIssuesListItem_NestedSubIssues)
  const subIssuesSummaryId = `${optimisticId}-subIssuesSummary`
  const subIssuesSummary = store.create(subIssuesSummaryId, 'SubIssuesSummary')
  subIssuesSummary.setValue(0, 'total')
  newSubIssue.setLinkedRecord(subIssuesSummary, 'subIssuesSummary')

  return newSubIssue
}

/**
 * Updates the parent's subIssuesSummary.total count.
 */
function updateParentSubIssuesSummary(
  store: RecordSourceProxy,
  parentRecord: NonNullable<ReturnType<RecordSourceProxy['get']>>,
  parentIssueId: string,
) {
  let parentSubIssuesSummary = parentRecord.getLinkedRecord('subIssuesSummary')
  if (!parentSubIssuesSummary) {
    const parentSummaryId = `${parentIssueId}-subIssuesSummary`
    parentSubIssuesSummary = store.create(parentSummaryId, 'SubIssuesSummary')
    const parentSubIssuesSummaryData: SubIssueSummaryData = {total: 0, completed: 0, percentCompleted: 0}
    for (const key in parentSubIssuesSummaryData) {
      parentSubIssuesSummary.setValue(parentSubIssuesSummaryData[key as keyof SubIssueSummaryData], key)
    }
    parentRecord.setLinkedRecord(parentSubIssuesSummary, 'subIssuesSummary')
  }
  const currentTotal = parentSubIssuesSummary.getValue('total')
  if (typeof currentTotal === 'number') {
    parentSubIssuesSummary.setValue(currentTotal + 1, 'total')
  } else {
    parentSubIssuesSummary.setValue(1, 'total')
  }
}

/**
 * Optimistic updater for creating a sub-issue. Adds the new issue to the parent's
 * subIssues connection immediately for responsive UI.
 * @internal Exported for testing purposes
 */
export function optimisticUpdateSubIssuesConnection(store: RecordSourceProxy, inputHash: CreateIssueInput) {
  if (!inputHash.parentIssueId) return

  const parentRecord = store.get(inputHash.parentIssueId)
  if (!parentRecord) return

  const subIssuesConnection = getOrCreateSubIssuesConnection(store, parentRecord)
  if (!subIssuesConnection) return

  // Create the optimistic sub-issue record
  const newSubIssue = createOptimisticSubIssue(store, inputHash, parentRecord)

  // Add the new sub-issue to the connection
  const currentNodes = subIssuesConnection.getLinkedRecords('nodes') || []
  subIssuesConnection.setLinkedRecords([...currentNodes, newSubIssue], 'nodes')

  // Update the totalCount on the connection
  const currentTotalCount = subIssuesConnection.getValue('totalCount')
  if (typeof currentTotalCount === 'number') {
    subIssuesConnection.setValue(currentTotalCount + 1, 'totalCount')
  } else {
    subIssuesConnection.setValue(1, 'totalCount')
  }

  updateParentSubIssuesSummary(store, parentRecord, inputHash.parentIssueId)
}

/**
 * Updater that runs after the server response. Adds the new issue to the parent's
 * subIssues connection if not already present (handles deduplication).
 * @internal Exported for testing purposes
 */
export function updateSubIssuesConnectionAfterResponse(store: RecordSourceSelectorProxy, inputHash: CreateIssueInput) {
  if (!inputHash.parentIssueId) return

  const payload = store.getRootField('createIssue')
  if (!payload) return

  const newIssue = payload.getLinkedRecord('issue')
  if (!newIssue) return

  const parentRecord = store.get(inputHash.parentIssueId)
  if (!parentRecord) return

  const subIssuesConnection = getOrCreateSubIssuesConnection(store, parentRecord)
  if (!subIssuesConnection) return

  // Check if the new issue is already in the connection
  const currentNodes = subIssuesConnection.getLinkedRecords('nodes') || []
  const newIssueId = newIssue.getValue('id')
  const alreadyExists = currentNodes.some(node => node?.getValue('id') === newIssueId)

  if (!alreadyExists) {
    // Add the new issue to the connection
    subIssuesConnection.setLinkedRecords([...currentNodes, newIssue], 'nodes')

    // Update totalCount
    const currentTotalCount = subIssuesConnection.getValue('totalCount')
    if (typeof currentTotalCount === 'number') {
      subIssuesConnection.setValue(currentTotalCount + 1, 'totalCount')
    } else {
      subIssuesConnection.setValue(currentNodes.length + 1, 'totalCount')
    }
  }
}

export function commitCreateIssueMutation({environment, input: inputHash, onError, onCompleted}: IssueMutationProps) {
  return commitMutation<createIssueMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation createIssueMutation($input: CreateIssueInput!, $fetchParent: Boolean = false) @raw_response_type {
        createIssue(input: $input) {
          issue {
            databaseId
            repository {
              databaseId
              id
              name
              owner {
                login
              }
            }
            number
            title
            id
            number
            url
            # Fetch fields needed for SubIssuesListItem when this is a sub-issue
            ...SubIssuesListItem @include(if: $fetchParent)
            # If this issue was a sub-issue of another issue, then we also fetch the parent's information so
            # that the UI will automatically update with the new sub-issues count.
            parent @include(if: $fetchParent) {
              id
              subIssuesSummary {
                total
                completed
                percentCompleted
              }
            }
          }
          errors {
            message
          }
        }
      }
    `,
    variables: {
      input: inputHash,
      fetchParent: !!inputHash.parentIssueId,
    },
    optimisticUpdater: store => optimisticUpdateSubIssuesConnection(store, inputHash),
    updater: store => updateSubIssuesConnectionAfterResponse(store, inputHash),
    onError: error => onError && onError(error),
    onCompleted: response => {
      onCompleted?.(response)
      incrementIssuesTabCounter()
    },
  })
}

export function commitCreateIssueMutationWithPromise({environment, issueMetadata}: IssueMutationWithPromiseProps) {
  // Convert metadata mapping to input hash
  const input: CreateIssueInput = {
    repositoryId: issueMetadata.repositoryId,
    title: issueMetadata.title,
    body: issueMetadata.body,
    labelIds: issueMetadata.labelIds,
    assigneeIds: issueMetadata.assigneeIds,
    milestoneId: issueMetadata.milestoneId,
    issueTypeId: issueMetadata.issueTypeId,
    parentIssueId: issueMetadata.parentIssueId,
    projectIds: issueMetadata.projectIds,
  }

  return new Promise<IssueMutationResponse>((resolve, reject) => {
    commitCreateIssueMutation({
      environment,
      input,
      onError: reject,
      onCompleted: response => resolve(extractMutationResponse(response)),
    })
  })
}
